import React, { useEffect, useMemo, useState, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  TrendingUp,
  FileText,
  Tent,
  MapPin,
  Users,
  Ticket,
  ClipboardList,
  ShoppingCart,
  Search,
  Settings,
  Database,
  User,
  CreditCard,
  Building2,
  DollarSign,
  Activity,
  Menu,
  X
} from 'lucide-react';
import AnalyticsDashboard from './components/Panels/AnalyticsDashboard';
import { useAuthStore } from '../../store/authStore';
import DashboardPanel from './components/Panels/DashboardPanel';
import ReportsAndExports from './components/Panels/ReportsAndExports';
import EventManagementPanel from './components/Panels/EventManagementPanel';
import VenueManagementPanel from './components/Panels/VenueManagementPanel';
import UserManagementPanel from './components/Panels/UserManagementPanel';
import OrderManagementPanel from './components/Panels/OrderManagementPanel';
import SystemMonitoring from './components/Panels/SystemMonitoring';
import EventCreationWizard from '../EventCreationWizard';
import VenueCreationWizard from '../VenueCreationWizard';
import QRScannerPage from '../QRScannerPage';
import PosAgentManager from './components/Panels/PosAgentManager';
import SeatMapsPanel from './components/Panels/SeatMapsPanel';
import RoleRequestsPanel from './components/Panels/RoleRequestsPanel';
import { eventsAPI, adminAPI } from '../../services/api';
import { extractRoleFromUser, normalizeRole } from '../../utils/roles';

import './DashboardPage.css';

const roleCapabilities = {
  ADMIN: {
    sections: ['system', 'events', 'actions', 'activity'],
    tabs: ['overview', 'analytics']
  },
  PLANNER: {
    sections: ['system', 'events', 'activity'],
    tabs: ['overview']
  },
  PROMOTER: {
    sections: ['system', 'events', 'activity'],
    tabs: ['overview']
  },
  STAFF: {
    sections: ['system', 'activity'],
    tabs: ['overview']
  }
};

const roleLabels = {
  ADMIN: 'Admin',
  PLANNER: 'Planner',
  PROMOTER: 'Promoter',
  STAFF: 'Staff'
};

const navigationSchema = [
  {
    section: 'Overview',
    items: [
      { key: 'overview', path: '/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard', exact: true, roles: ['ADMIN', 'PLANNER', 'PROMOTER', 'STAFF'] },
      { key: 'analytics', path: '/dashboard/analytics', icon: <TrendingUp size={18} />, label: 'Analytics', roles: ['ADMIN'] },
      { key: 'reports', path: '/dashboard/reports', icon: <FileText size={18} />, label: 'Reports', roles: ['ADMIN', 'PLANNER', 'PROMOTER'] }
    ]
  },
  {
    section: 'Management',
    items: [
      { key: 'events', path: '/dashboard/events', icon: <Tent size={18} />, label: 'Events', roles: ['ADMIN', 'PLANNER', 'PROMOTER'] },
      { key: 'seat-maps', path: '/dashboard/seat-maps', icon: <MapPin size={18} />, label: 'Seat Maps', roles: ['ADMIN', 'PLANNER'] },
      { key: 'venues', path: '/dashboard/venues', icon: <Building2 size={18} />, label: 'Venues', roles: ['ADMIN', 'PLANNER', 'PROMOTER'] },
      { key: 'users', path: '/dashboard/users', icon: <Users size={18} />, label: 'Users', roles: ['ADMIN'] },
      { key: 'pos-agents', path: '/dashboard/pos-agents', icon: <Ticket size={18} />, label: 'Agents (POS)', roles: ['ADMIN', 'PLANNER'] },
      { key: 'role-requests', path: '/dashboard/role-requests', icon: <ClipboardList size={18} />, label: 'Role Requests', roles: ['ADMIN', 'PLANNER'] },
      { key: 'orders', path: '/dashboard/orders', icon: <ShoppingCart size={18} />, label: 'Orders', roles: ['ADMIN'] },
      { key: 'planner-orders', path: '/dashboard/planner-orders', icon: <ShoppingCart size={18} />, label: 'Orders', roles: ['PLANNER'] },
    ]
  },
  {
    section: 'System',
    items: [
      { key: 'monitoring', path: '/dashboard/monitoring', icon: <Activity size={18} />, label: 'Monitoring', roles: ['ADMIN'] },
      { key: 'settings', path: '/dashboard/settings', icon: <Settings size={18} />, label: 'Settings', roles: ['ADMIN'] },
      { key: 'logs', path: '/dashboard/logs', icon: <ClipboardList size={18} />, label: 'Logs', roles: ['ADMIN'] },
      { key: 'backups', path: '/dashboard/backups', icon: <Database size={18} />, label: 'Backups', roles: ['ADMIN'] }
    ]
  },
  {
    section: 'Operations',
    items: [
      { key: 'scan', path: '/dashboard/scan', icon: <Search size={18} />, label: 'Scan Tickets', roles: ['STAFF', 'ADMIN', 'PLANNER'] }
    ]
  }
];

const quickActionSchema = [
  {
    icon: <Tent size={20} />,
    title: 'Create Event',
    description: 'Add a new event to the platform',
    path: '/dashboard/events/create',
    roles: ['ADMIN', 'PLANNER']
  },
  {
    icon: <Building2 size={20} />,
    title: 'Create Venue',
    description: 'Add a new venue to the platform',
    path: '/dashboard/venues/create',
    roles: ['ADMIN', 'PLANNER']
  },
  {
    icon: <Users size={20} />,
    title: 'Manage Users',
    description: 'View and manage user accounts',
    path: '/dashboard/users',
    roles: ['ADMIN']
  },
  {
    icon: <TrendingUp size={20} />,
    title: 'View Reports',
    description: 'Generate detailed analytics reports',
    path: '/dashboard/reports',
    roles: ['ADMIN', 'PLANNER', 'PROMOTER']
  },
  {
    icon: <Settings size={20} />,
    title: 'System Settings',
    description: 'Configure platform settings',
    path: '/dashboard/settings',
    roles: ['ADMIN']
  }
];

const viewComponents = {
  analytics: {
    roles: ['ADMIN'],
    render: (props) => <AnalyticsDashboard {...props} />
  },
  reports: {
    roles: ['ADMIN', 'PLANNER', 'PROMOTER'],
    render: () => <ReportsAndExports />
  },
  events: {
    roles: ['ADMIN', 'PLANNER', 'PROMOTER'],
    render: () => <EventManagementPanel />
  },
  'events/create': {
    roles: ['ADMIN', 'PLANNER'],
    render: () => <EventCreationWizard />
  },
  'seat-maps': {
    roles: ['ADMIN', 'PLANNER'],
    render: () => <SeatMapsPanel />
  },
  venues: {
    roles: ['ADMIN', 'PLANNER', 'PROMOTER'],
    render: () => <VenueManagementPanel />
  },
  'venues/create': {
    roles: ['ADMIN', 'PLANNER'],
    render: () => <VenueCreationWizard />
  },
  users: {
    roles: ['ADMIN', 'PLANNER'],
    render: () => <UserManagementPanel />
  },
  orders: {
    roles: ['ADMIN'],
    render: () => <OrderManagementPanel />
  },
  'planner-orders': {
    roles: ['PLANNER'],
    render: () => <OrderManagementPanel mode="planner" />
  },
  'pos-agents': {
    roles: ['ADMIN', 'PLANNER'],
    render: () => <PosAgentManager />
  },
  'role-requests': {
    roles: ['ADMIN', 'PLANNER'],
    render: () => <RoleRequestsPanel />
  },
  monitoring: {
    roles: ['ADMIN'],
    render: () => <SystemMonitoring />
  },
  settings: {
    roles: ['ADMIN'],
    render: () => (
      <div className="dashboard-placeholder">
        <h2>System Settings</h2>
        <p>Platform configuration tools are coming soon.</p>
      </div>
    )
  },
  logs: {
    roles: ['ADMIN'],
    render: () => (
      <div className="dashboard-placeholder">
        <h2>System Logs</h2>
        <p>Audit trails and system logs will appear here.</p>
      </div>
    )
  },
  backups: {
    roles: ['ADMIN'],
    render: () => (
      <div className="dashboard-placeholder">
        <h2>Backup Management</h2>
        <p>Manage system backups and recovery plans.</p>
      </div>
    )
  },
  scan: {
    roles: ['STAFF', 'ADMIN', 'PLANNER'],
    render: () => <QRScannerPage />
  }
};

const getPersistedUser = () => {
  try {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

const DashboardPage = ({ roleOverride, forcedRole }) => {
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [systemData, setSystemData] = useState(null);
  const [topEvents, setTopEvents] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Layout State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const previousOverflowRef = useRef('');
  const navRef = useRef(null);
  const navScrollPosition = useRef(0);

  const role = useMemo(() => {
    const persistedUser = user || getPersistedUser();
    const normalizedForced = normalizeRole(forcedRole);
    if (normalizedForced) return normalizedForced;

    const normalizedOverride = normalizeRole(roleOverride);
    if (normalizedOverride) return normalizedOverride;

    const fromUser = extractRoleFromUser(persistedUser);
    return fromUser || 'STAFF';
  }, [forcedRole, roleOverride, user]);

  const displayName = useMemo(() => {
    const persistedUser = user || getPersistedUser();
    const parts = [persistedUser?.firstName, persistedUser?.lastName].filter(Boolean);
    return parts.join(' ').trim() || 'User';
  }, [user]);

  const pathSegments = location.pathname.replace(/^\/(dashboard|admin|planner|promoter)\/?/, '').split('/').filter(Boolean);
  const viewKey = pathSegments.join('/') || 'overview';


  const capabilities = roleCapabilities[role] || roleCapabilities.STAFF;
  const brandTitle = `TikPlat ${roleLabels[role] || 'Team'}`;
  const brandSubtitle = 'Control Center';

  const navigationWithBadges = useMemo(() => {
    // Filter by role first
    const filteredSections = navigationSchema
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.roles || item.roles.includes(role))
      }))
      .filter((section) => section.items.length > 0);

    // Apply badges if systemData exists
    if (!systemData) return filteredSections;

    return filteredSections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        if (item.key === 'events') {
          return { ...item, badge: systemData.totalEvents };
        }
        return item;
      })
    }));
  }, [role, systemData]);

  const quickActions = useMemo(
    () =>
      quickActionSchema
        .filter((action) => !action.roles || action.roles.includes(role))
        .map((action) => ({
          ...action,
          action: () => navigate(action.path)
        })),
    [role, navigate]
  );

  // Layout Effects
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous || '';
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const bodyStyle = document.body.style;
    const bodyClassList = document.body.classList;

    if (sidebarOpen && window.innerWidth <= 1024) {
      previousOverflowRef.current = bodyStyle.overflow;
      bodyStyle.overflow = 'hidden';
      bodyClassList.add('dashboard-sidebar-open');
      return () => {
        bodyStyle.overflow = previousOverflowRef.current || '';
        bodyClassList.remove('dashboard-sidebar-open');
      };
    }

    bodyStyle.overflow = previousOverflowRef.current || '';
    bodyClassList.remove('dashboard-sidebar-open');
    return undefined;
  }, [sidebarOpen]);

  useLayoutEffect(() => {
    if (navRef.current) {
      navRef.current.scrollTop = navScrollPosition.current || 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    const navEl = navRef.current;
    if (!navEl) {
      return undefined;
    }
    const handleNavScroll = () => {
      navScrollPosition.current = navEl.scrollTop || 0;
    };
    navEl.addEventListener('scroll', handleNavScroll);
    return () => navEl.removeEventListener('scroll', handleNavScroll);
  }, []);

  // Data Loading Effects
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true, state: { from: location } });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const systemOverview = {
        totalRevenue: 0,
        totalUsers: 0,
        totalEvents: 0,
        activeEvents: 0,
        totalOrders: 0,
        pendingOrders: 0,
        ticketsSold: 0,
        serverStatus: 'healthy',
        databaseStatus: 'healthy',
        paymentGatewayStatus: 'healthy'
      };

      try {
        const analytics = await eventsAPI.getAnalytics();
        const analyticsData = analytics?.data ?? analytics;
        if (analyticsData) {
          systemOverview.totalRevenue = analyticsData.totalRevenue || 0;
          systemOverview.totalEvents = analyticsData.totalEvents || 0;
          systemOverview.activeEvents = analyticsData.activeEvents || 0;
          systemOverview.ticketsSold = (analyticsData.events || []).reduce((sum, e) => sum + (Number(e.ticketsSold) || 0), 0);

          if (typeof analyticsData.averageOccupancy === 'number') {
            systemOverview.avgOccupancy = analyticsData.averageOccupancy;
          } else {
            const occs = (analyticsData.events || [])
              .map(e => e.occupancy)
              .filter(o => typeof o === 'number');
            systemOverview.avgOccupancy = occs.length ? (occs.reduce((a, b) => a + b, 0) / occs.length) : 0;
          }
          setTopEvents((analyticsData.events || []).map(evt => ({
            id: evt.eventId,
            title: evt.title,
            date: evt.startTime,
            revenue: evt.revenue,
            ticketsSold: Number(evt.ticketsSold) || 0,
            capacity: evt.capacity != null ? Number(evt.capacity) : undefined,
            occupancy: typeof evt.occupancy === 'number' ? evt.occupancy : undefined
          })));

        }
      } catch (eventError) {
        console.error('Unable to load events for dashboard stats:', eventError);
      }

      if (role === 'ADMIN') {
        try {
          const usersResponse = await adminAPI.getUsers({ page: 0, size: 1, sort: 'createdDate,desc' });
          const usersData = usersResponse?.data ?? usersResponse;
          if (typeof usersData?.totalElements === 'number') {
            systemOverview.totalUsers = usersData.totalElements;
          } else if (Array.isArray(usersData?.content)) {
            systemOverview.totalUsers = usersData.content.length;
          } else if (Array.isArray(usersData)) {
            systemOverview.totalUsers = usersData.length;
          }
        } catch (userError) {
          console.error('Unable to load users for dashboard stats:', userError);
        }
      }

      const activity = [
        {
          id: 1,
          type: 'event',
          icon: <Tent size={18} />,
          title: 'New Event Created',
          description: 'Sarah Johnson created "Jazz Night at Blue Note"',
          time: '2 minutes ago'
        },
        {
          id: 2,
          type: 'user',
          icon: <User size={18} />,
          title: 'User Registration',
          description: 'New user Mike Chen registered as Event Planner',
          time: '15 minutes ago'
        },
        {
          id: 3,
          type: 'order',
          icon: <CreditCard size={18} />,
          title: 'Large Order Completed',
          description: 'Order #TK7B9X for $2,450 completed successfully',
          time: '32 minutes ago'
        },
        {
          id: 4,
          type: 'system',
          icon: <Settings size={18} />,
          title: 'System Maintenance',
          description: 'Automated cleanup released 145 expired ticket reservations',
          time: '1 hour ago'
        }
      ];

      setSystemData(systemOverview);
      setRecentActivity(activity);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);

  const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);

  const quickStats = systemData ? [
    {
      color: '#10b981',
      icon: <DollarSign size={20} />,
      trend: '+12.5%',
      value: formatCurrency(systemData.totalRevenue),
      label: 'Total Revenue'
    },
    {
      color: '#3b82f6',
      icon: <Ticket size={20} />,
      trend: '+8.3%',
      value: formatNumber(systemData.ticketsSold || 0),
      label: 'Tickets Sold'
    },
    {
      color: '#8b5cf6',
      icon: <Tent size={20} />,
      trend: '+5.2%',
      value: formatNumber(systemData.totalEvents || 0),
      label: 'Events'
    },
    {
      color: '#f59e0b',
      icon: <Users size={20} />,
      trend: '+15.7%',
      value: formatNumber(systemData.totalUsers || 0),
      label: 'Active Users'
    }
  ] : [];

  // Helper Functions
  const isActivePath = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const getCurrentPageTitle = () => {
    for (const section of navigationWithBadges) {
      for (const item of section.items) {
        if (isActivePath(item.path, item.exact)) {
          return item.label;
        }
      }
    }
    return brandTitle;
  };

  const getBreadcrumb = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length <= 1) {
      return [];
    }
    return pathParts.map((part, index) => {
      if (index === 0) {
        return brandTitle;
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    });
  };

  const currentViewConfig = viewComponents[viewKey];
  const hasAccessToView = currentViewConfig
    ? !currentViewConfig.roles || currentViewConfig.roles.includes(role)
    : true;

  const renderOverview = () => (
    <DashboardPanel
      capabilities={capabilities}
      systemData={systemData}
      topEvents={topEvents}
      quickActions={quickActions}
      recentActivity={recentActivity}
      formatCurrency={formatCurrency}
      formatNumber={formatNumber}
      loadDashboardData={loadDashboardData}
      navigate={navigate}
    />
  );

  const renderSecondaryView = () => {
    if (!currentViewConfig) {
      return <div className="admin-dashboard-loading">This section is under construction.</div>;
    }

    if (!hasAccessToView) {
      return <div className="admin-dashboard-loading">You do not have access to this section.</div>;
    }

    if (viewKey === 'analytics') {
      return (
        <div className="admin-dashboard-grid">
          <AnalyticsDashboard
            summary={systemData}
            events={topEvents}
            formatCurrency={formatCurrency}
            formatNumber={formatNumber}
          />
        </div>
      );
    }

    return <div className="admin-dashboard-grid single-view">{currentViewConfig.render({})}</div>;
  };

  const renderContent = () => {
    if (loading && viewKey === 'overview') {
      return <div className="admin-dashboard-loading">Loading dashboard...</div>;
    }

    if (viewKey === 'overview') {
      return renderOverview();
    }

    return renderSecondaryView();
  };

  return (
    <div className="dashboard-shell">
      {/* Sidebar Logic */}
      <aside className={`dashboard-control-center ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="control-center-header">
          <div className="dashboard-logo">
            <span className="icon"><Ticket size={24} /></span>
            <span>{brandTitle}</span>
          </div>
          <div className="dashboard-subtitle">{brandSubtitle}</div>
        </div>

        <nav className="dashboard-nav" ref={navRef}>
          {navigationWithBadges.map((section, sectionIndex) => (
            <div className="dashboard-nav-section" key={sectionIndex}>
              <div className="section-title">{section.section}</div>
              {section.items.map((item, itemIndex) => (
                <div className="dashboard-nav-item" key={itemIndex}>
                  <div
                    className={`dashboard-nav-link ${isActivePath(item.path, item.exact) ? 'active' : ''}`}
                    onClick={() => {
                      navigate(item.path);
                      if (window.innerWidth <= 1024) {
                        setSidebarOpen(false);
                      }
                    }}
                  >
                    <span className="dashboard-nav-icon">{item.icon}</span>
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="dashboard-nav-badge">
                        {typeof item.badge === 'number' && item.badge > 999
                          ? `${Math.floor(item.badge / 1000)}k`
                          : item.badge}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* Overlay */}
      <div
        className={`dashboard-sidebar-overlay ${sidebarOpen ? 'is-open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Main Content Area */}
      <div className={`dashboard-main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {/* Topbar */}
        <div className="dashboard-topbar">
          <div className="topbar-left">
            <button
              className="menu-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            <div>
              <div className="page-title">{getCurrentPageTitle()}</div>
              {getBreadcrumb().length > 0 && (
                <div className="breadcrumb">
                  {getBreadcrumb().map((crumb, index) => (
                    <React.Fragment key={index}>
                      <span>{crumb}</span>
                      {index < getBreadcrumb().length - 1 && (
                        <span className="separator">›</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Optional: Add user profile logic here if necessary, but existing DashboardPage had user strip in content */}
        </div>

        {/* Scrollable Content Area */}
        <div className="dashboard-content-area">
          <div className="admin-dashboard">
            {viewKey === 'overview' && (
              <div className="dashboard-user-strip">
                <div className="user-avatar-chip">
                  {displayName?.[0] || 'U'}
                </div>
                <div className="user-strip-meta">
                  <div className="user-strip-name">{displayName}</div>
                  <div className="user-strip-role">{roleLabels[role] || 'Team'} • Signed in</div>
                </div>
              </div>
            )}
            <div className="admin-tab-panel">
              {viewKey === 'overview' && quickStats.length > 0 && (
                <div className="dashboard-quick-stats">
                  {quickStats.map((card) => (
                    <div className="dashboard-stat-card" key={card.label} style={{ borderLeftColor: card.color }}>
                      <div className="stat-header">
                        <span className="icon">{card.icon}</span>
                        <span className="trend up">{card.trend}</span>
                      </div>
                      <div className="stat-value">{card.value}</div>
                      <div className="stat-label">{card.label}</div>
                    </div>
                  ))}
                </div>
              )}
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
